import { useState, useEffect } from "react";
import moment from "moment";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCountries } from "../../../hooks/useData";
import { Drawer, Button as AntButton, Card, Select as AntSelect, DatePicker as AntDatePicker, Input as AntInput } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';

import Api from "../../../services/api";
import AppSumary from "./overview/appSumary";
import AppNetwork from "./overview/appNetwork";
import AppRetentionPerformance from "./overview/appRetentionPerformance";
import AppCampaignPerformance from "./overview/appCampaignPerformance";
import AppAdPerformance from "./overview/appAdPerformance";
import AppAdsetPerformance from "./overview/appAdsetPerformance";
import AppChartCountry from "./overview/appChartCountry";
import { END_DATE, START_DATE } from "../../../utility";

function AppOverview() {
  const { code, os } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries } = useCountries();
  
  const [adNetworkOptions, setAdNetworkOptions] = useState([]);
  // Chuẩn hoá dữ liệu country cho Antd Select
  const countryOptions = (countries || []).map((c) => ({
    value: c?.value ?? c?.code,
    label: c?.label ?? c?.name,
  }));

  const [startDate, setStartDate] = useState(
    searchParams.get('startDate') ? moment(searchParams.get('startDate')) : moment().subtract(START_DATE, "days")
  );
  const [endDate, setEndDate] = useState(
    searchParams.get('endDate') ? moment(searchParams.get('endDate')) : moment().subtract(END_DATE, "days")
  );
  const [campaign, setCampaign] = useState(searchParams.get('campaign') || "");
  const [country, setCountry] = useState(searchParams.get('country') || "");
  const [adnetwork, setAdnetwork] = useState(searchParams.get('adnetwork') || "");
  const [typeAdnetwork, setTypeAdnetwork] = useState("cost");
  const [typeRetention, setTypeRetention] = useState(1);
  const [chartCountry, setChartCountry] = useState("cost");
  const [sortValueCampaign, setSortValueCampaign] = useState("value");
  const [sortValueAd, setSortValueAd] = useState("value");
  const [load, setLoad] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filterAdnetwork = [
    { code: "cost", name: "Cost" },
    { code: "install", name: "Install" },
    { code: "ecpi", name: "Ecpi" },
    { code: "ecpm", name: "Ecpm" },
  ];

  const filterRetention = [
    { code: 0, name: "D0" },
    { code: 1, name: "D1" },
    { code: 3, name: "D3" },
    { code: 7, name: "D7" },
  ];

  const sortBy = [
    { code: "value", name: "Sort By Value" },
    { code: "change", name: "Sort By Change" },
  ];

  useEffect(() => {
    // Load ad networks list from API
    (async () => {
      try {
        const res = await Api.get("/api/filter/ad_network");
        const raw = Array.isArray(res?.data?.data) ? res.data.data : [];
        const opts = raw.map((n) => {
          const networkCode = n.code || '';
          const networkName = n.name || n.code || '';
          const icon = n.icon || '';
          return {
            value: networkCode,
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {icon && (
                  <img
                    src={icon}
                    alt={networkName}
                    style={{ width: 16, height: 16, objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <span>{networkName}</span>
              </span>
            )
          };
        });
        setAdNetworkOptions(opts);
      } catch (e) {
        toast.error("Không tải được danh sách Ad Networks");
        setAdNetworkOptions([]);
      }
    })();
  }, []);

  const updateURLParams = () => {
    const params = {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      ...(campaign && { campaign }),
      ...(country && { country }),
      ...(adnetwork && { adnetwork })
    };
    setSearchParams(params);
  };

  const handleReset = () => {
    setDrawerOpen(false);
    setStartDate(moment().subtract(START_DATE, "days"));
    setEndDate(moment().subtract(END_DATE, "days"));
    setCampaign("");
    setCountry("");
    setAdnetwork("");
    setSearchParams({});
    setLoad(!load); 
  };

  const handleLoading = () => {
    setDrawerOpen(false);
    updateURLParams();
    setLoad(!load); 
  };

  const handleChangeDate = (dates) => {
    if (!dates || dates.length !== 2) return;
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const onChangeCampaign = (e) => {
    setCampaign(e.target.value); 
  };

  const onChangeCountry = (val) => {
    setCountry(val || ""); 
  };

  const onChangeAdnetwork = (val) => {
    setAdnetwork(val || ""); 
  };

  const onChangeTypeAdnetwork = (val) => {
    setTypeAdnetwork(val);
  };

  const onChangeTypeRetention = (val) => {
    setTypeRetention(val);
  };

  const onChangeSortByCampaign = (val) => {
    setSortValueCampaign(val);
  };

  const onChangeSortByAd = (val) => {
    setSortValueAd(val);
  };

  const onChangeChartCountry = (val) => {
    setChartCountry(val);
  };

  return (
    <>
      <div className="background-second overview-scope">
        {/* Mobile: Search icon button */}
        {!drawerOpen && (
          <div className="mobile-search-toggle" style={{ 
            display: 'none',
            position: 'fixed',
            right: '16px',
            top: '54px',
            zIndex: 1002,
          }}>
            <AntButton
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            />
          </div>
        )}

        <section className="main__filter">
          <div className="filter">
            <div className="container-fluid">
              {/* Desktop: Show filters normally */}
              <div className="desktop-filter">
                <div className="d-flex flex-row-reverse">
                  <div>
                    <AntButton type="primary" className="ml-3" onClick={handleLoading}>
                      Apply
                    </AntButton>
                    <AntButton className="ml-3" onClick={handleReset}>
                      Reset
                    </AntButton>
                  </div>
                  <div className="form-group flex-fill ms-2" style={{ marginBottom: 0 }}>
                    <div className="filter-sticky">
                      <div className="d-flex justify-content-end">
                        <AntDatePicker.RangePicker
                          value={[dayjs(startDate), dayjs(endDate)]}
                          onChange={handleChangeDate}
                          allowClear={false}
                          format="YYYY-MM-DD"
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-fill ms-2" style={{ minWidth: 220 }}>
                    <AntInput
                      placeholder="Campaign Name"
                      value={campaign}
                      onChange={onChangeCampaign}
                    />
                  </div>
                  <div className="flex-fill ms-2" style={{ minWidth: 220 }}>
                    <AntSelect
                      showSearch
                      allowClear
                      placeholder="All Countries"
                      options={countryOptions}
                      value={country || undefined}
                      onChange={onChangeCountry}
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="flex-fill ms-2" style={{ minWidth: 200 }}>
                    <AntSelect
                      allowClear
                      placeholder="All Ad Networks"
                      options={adNetworkOptions}
                      value={adnetwork || undefined}
                      onChange={onChangeAdnetwork}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile: Drawer with filters */}
        <Drawer
          title="Search Filters"
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={320}
          className="mobile-filter-drawer"
          zIndex={2500}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <AntSelect
              allowClear
              placeholder="All Ad Networks"
              options={adNetworkOptions}
              value={adnetwork || undefined}
              onChange={onChangeAdnetwork}
              style={{ width: "100%" }}
            />

            <AntSelect
              showSearch
              allowClear
              placeholder="All Countries"
              options={countryOptions}
              value={country || undefined}
              onChange={onChangeCountry}
              optionFilterProp="label"
              style={{ width: "100%" }}
            />

            <AntInput
              placeholder="Campaign Name"
              value={campaign}
              onChange={onChangeCampaign}
            />

            <div className="filter-sticky">
              <AntDatePicker.RangePicker
                value={[dayjs(startDate), dayjs(endDate)]}
                onChange={handleChangeDate}
                allowClear={false}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="drawer-date-dropdown"
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                className="btn btn-primary"
                onClick={handleLoading}
                style={{ flex: 1 }}
              >
                Apply
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleReset}
                style={{ flex: 1 }}
              >
                Reset
              </button>
            </div>
          </div>
        </Drawer>

        <style>{`
          /* Card selects unified width and bold text (PC & Mobile) */
          .card-select { width: 120px !important; }
          .card-select .ant-select-selection-item { font-weight: 600 !important; }

          /* Summary card styles scoped to overview */
          .overview-scope .card-summary { display: flex; flex-direction: column; height: 100%; }
          .overview-scope .card-summary .money { font-weight: 700; font-size: 22px; line-height: 1.2; }
          .overview-scope .card-summary .fa-sort-down { color: #f5222d; margin: 0 4px; }
          .overview-scope .card-summary .fa-sort-up { color: #52c41a; margin: 0 4px; }
          .overview-scope .sumary-dash .card-summary:last-child { height: 100%; display: flex; flex-direction: column; justify-content: center; }
          .overview-scope p.last { margin: 15px 0 !important; }

          /* CSS cho table trong Card - áp dụng cho tất cả Card */
          .overview-scope .ant-card .fa-sort-up { color: #0C8F00; }
          .overview-scope .ant-card .fa-sort-down { color: #EE1704; }
          .overview-scope .ant-card .fa-sort-up::before,
          .overview-scope .ant-card .fa-sort-down::before {
            display: table-cell;
            line-height: 0;
          }
          .overview-scope .ant-card .fa-sort-down::before {
            line-height: 1;
          }
          .overview-scope .ant-card .flex-column .fa-sort-up::before,
          .overview-scope .ant-card .flex-column .fa-sort-down::before {
            line-height: 0.2;
          }
          .overview-scope .ant-card .flex-column .fa-sort-up.active,
          .overview-scope .ant-card .flex-column .fa-sort-down.active {
            color: #109CF1;
          }
          .overview-scope .ant-card table {
            overflow-x: scroll;
          }
          .overview-scope .ant-card th,
          .overview-scope .ant-card td {
            max-width: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 300px;
          }
          .overview-scope .ant-card th {
            background: #F5F5F5;
          }
          .overview-scope .ant-card th:hover {
            background: #cbcbcb;
            cursor: pointer;
          }
          .overview-scope .ant-card th .fa {
            color: #B5B5C3;
            cursor: pointer;
          }
          .overview-scope .ant-card .fa-filter,
          .overview-scope .ant-card .fa-sort {
            font-size: 12px;
          }
          .overview-scope .ant-card .table-responsive {
            display: block;
            overflow-x: auto;
            width: 100%;
          }
          .overview-scope .ant-card .table-responsive .fixed {
            background: #fff;
            min-height: 200px;
            left: 0;
            position: sticky;
          }
          .overview-scope .ant-card .table-responsive th.fixed {
            background: #F5F5F5;
          }

          /* Mobile styles */
          @media (max-width: 768px) {
            .main__content {
              margin-top: 0 !important;
            }
            .main__filter {
              display: none !important;
            }
            .desktop-filter {
              display: none !important;
            }
            .mobile-search-toggle {
              display: block !important;
            }
            
            /* Reset padding trong container-fluid */
            .overview-scope .container-fluid {
              padding-left: 15px !important;
              padding-right: 15px !important;
            }
            
            /* Reset padding của row */
            .overview-scope .container-fluid .row {
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            
            /* Fix responsive for charts on mobile - 1 column layout */
            .overview-scope .col-md-6,
            .overview-scope .col-md-3,
            .overview-scope .col.index-dash {
              width: 100% !important;
              max-width: 100% !important;
              flex: 0 0 100% !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              margin-top: 0 !important;
              margin-bottom: 15px !important;
            }
            
            /* Phần tử cuối cùng trong row không có margin-bottom */
            .overview-scope .col-md-6:last-child,
            .overview-scope .col-md-3:last-child,
            .overview-scope .col.index-dash:last-child {
              margin-bottom: 0 !important;
            }
            
            /* Khoảng cách trên dưới trong 1 row là 15px */
            .overview-scope .row.justify-content-between {
              width: 100% !important;
              max-width: 100% !important;
              margin-top: 15px !important;
              margin-bottom: 15px !important;
            }
            
            /* Row đầu tiên không có margin-top */
            .overview-scope .row.justify-content-between:first-child {
              margin-top: 0 !important;
            }
            
            /* Loại bỏ margin-bottom của row cuối */
            .overview-scope .row.justify-content-between:last-child {
              margin-bottom: 0 !important;
            }
            
            /* Khoảng cách giữa row đầu tiên (sumary-dash) và row tiếp theo */
            .overview-scope .sumary-dash {
              margin-top: 0 !important;
              margin-bottom: 15px !important;
            }
            
            /* Card styles - căn giữa với 15px mỗi bên */
            .overview-scope .ant-card {
              width: 100% !important;
              margin: 0 !important;
            }
            
            .box-chart {
              width: 100% !important;
              max-width: 100% !important;
              overflow: hidden;
              padding: 1rem;
            }
            .chart-data {
              width: 100% !important;
              max-width: 100% !important;
              overflow: hidden;
            }
            /* Fix chart container to fill box */
            .box-chart > div {
              width: 100% !important;
              max-width: 100% !important;
            }
            .box-chart canvas,
            .box-chart svg {
              width: 100% !important;
              max-width: 100% !important;
              height: auto !important;
            }
            /* Bold selected text in antd selects inside cards */
            .card-select .ant-select-selection-item {
              font-weight: 600 !important;
            }
            /* Ensure chart fills container */
            .box-chart .recharts-wrapper,
            .box-chart .recharts-surface {
              width: 100% !important;
              max-width: 100% !important;
            }
          }
          
          /* Desktop styles */
          @media (min-width: 769px) {
            .main__filter {
              display: block !important;
            }
            .desktop-filter {
              display: block !important;
            }
            .mobile-search-toggle {
              display: none !important;
            }
          }

          /* Force readable color in Antd selects */
          .overview-scope .ant-select-selector .ant-select-selection-item,
          .overview-scope .ant-select-item-option-content {
            color: #000 !important;
          }
          .overview-scope .ant-select-selection-placeholder {
            color: rgba(0,0,0,0.45) !important;
          }

          /* Ensure date dropdown in Drawer doesn't overflow width */
          .mobile-filter-drawer .drawer-date-dropdown {
            max-width: 100% !important;
          }
          .mobile-filter-drawer .drawer-date-dropdown .ant-picker-panels {
            display: flex;
            flex-wrap: wrap;
          }
        `}</style>

        <section className="main__content">
          <div className="container-fluid">
            <div className="row justify-content-between sumary-dash">
              <AppSumary
                startDate={startDate}
                endDate={endDate}
                code={code}
                os={os}
                campaign={campaign}
                adnetwork={adnetwork}
                country={country}
                reload={load}
              />
            </div>

            <div className="row justify-content-between mt-6">
              <div className="col-md-6" style={{ display: 'flex' }}>
                <Card size="small" bodyStyle={{ padding: 16 }} style={{ width: '100%', height: '100%' }}>
                  <div className="d-flex align-items-center h3" style={{ margin: 0 }}>
                    <AntSelect
                      size="small"
                      bordered={false}
                      className="mr-2 card-select"
                      options={filterAdnetwork.map(o => ({ value: o.code, label: o.name }))}
                      value={typeAdnetwork}
                      style={{ width: 140 }}
                      onChange={onChangeTypeAdnetwork}
                    />
                    By Adnetwork
                  </div>
                  <AppNetwork
                    startDate={startDate}
                    endDate={endDate}
                    code={code}
                    os={os}
                    campaign={campaign}
                    adnetwork={adnetwork}
                    country={country}
                    filter={typeAdnetwork}
                    reload={load}
                  />
                </Card>
              </div>
              <div className="col-md-6" style={{ display: 'flex' }}>
                <Card size="small" bodyStyle={{ padding: 16 }} style={{ width: '100%', height: '100%' }}>
                  <div className="d-flex align-items-center h3" style={{ margin: 0 }}>
                    <AntSelect
                      size="small"
                      bordered={false}
                      className="mr-2 card-select"
                      options={filterRetention.map(o => ({ value: o.code, label: o.name }))}
                      value={typeRetention}
                      style={{ width: 80 }}
                      onChange={onChangeTypeRetention}
                    />
                    Performence
                  </div>
                  <AppRetentionPerformance
                    startDate={startDate}
                    endDate={endDate}
                    code={code}
                    os={os}
                    campaign={campaign}
                    adnetwork={adnetwork}
                    country={country}
                    filter={typeRetention}
                    reload={load}
                  />
                </Card>
              </div>
            </div>

            <div className="row justify-content-between mt-6">
              <div className="col-md-6" style={{ display: 'flex' }}>
                <Card size="small" bodyStyle={{ padding: 16 }} style={{ width: '100%', height: '100%' }}>
                  <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 12 }}>
                    <p className="h3" style={{ margin: 0 }}>Campaign Perfomance</p>
                    <div className="d-flex align-items-center">
                      <AntSelect
                        size="small"
                        bordered={false}
                        className="card-select"
                        options={sortBy.map(o => ({ value: o.code, label: o.name }))}
                        value={sortValueCampaign}
                        style={{ width: 140 }}
                        onChange={onChangeSortByCampaign}
                      />
                      <i className="fa fa-expand ml-3"></i>
                    </div>
                  </div>
                  <AppCampaignPerformance
                    startDate={startDate}
                    endDate={endDate}
                    code={code}
                    os={os}
                    campaign={campaign}
                    adnetwork={adnetwork}
                    country={country}
                    sortValue={sortValueCampaign}
                    reload={load}
                  />
                </Card>
              </div>
              <div className="col-md-6" style={{ display: 'flex' }}>
                <Card size="small" bodyStyle={{ padding: 16 }} style={{ width: '100%', height: '100%' }}>
                  <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 12 }}>
                    <p className="h3" style={{ margin: 0 }}>Ad Perfomance</p>
                    <div className="d-flex align-items-center">
                      <AntSelect
                        size="small"
                        bordered={false}
                        className="card-select"
                        options={sortBy.map(o => ({ value: o.code, label: o.name }))}
                        value={sortValueAd}
                        style={{ width: 120 }}
                        onChange={onChangeSortByAd}
                      />
                      <i className="fa fa-expand ml-3"></i>
                    </div>
                  </div>
                  <AppAdPerformance
                    startDate={startDate}
                    endDate={endDate}
                    code={code}
                    os={os}
                    campaign={campaign}
                    adnetwork={adnetwork}
                    country={country}
                    sortValue={sortValueAd}
                    reload={load}
                  />
                </Card>
              </div>
            </div>

            <div className="row justify-content-between mt-6">
              <div className="col-md-6" style={{ display: 'flex' }}>
                <Card size="small" bodyStyle={{ padding: 16 }} style={{ width: '100%', height: '100%' }}>
                  <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 12 }}>
                    <p className="h3" style={{ margin: 0 }}>Adset Perfomance</p>
                    <div className="d-flex align-items-center">
                      <AntSelect
                        size="small"
                        bordered={false}
                        className="card-select"
                        options={sortBy.map(o => ({ value: o.code, label: o.name }))}
                        value={sortValueAd}
                        style={{ width: 120 }}
                        onChange={onChangeSortByAd}
                      />
                      <i className="fa fa-expand ml-3"></i>
                    </div>
                  </div>
                  <AppAdsetPerformance
                    startDate={startDate}
                    endDate={endDate}
                    code={code}
                    os={os}
                    campaign={campaign}
                    adnetwork={adnetwork}
                    country={country}
                    sortValue={sortValueAd}
                    reload={load}
                  />
                </Card>
              </div>
            </div>

            <div className="row justify-content-between mt-6">
              <div className="col-md-6">
                <Card size="small" bodyStyle={{ padding: 16 }} style={{ width: '100%' }}>
                  <div className="d-flex align-items-center h3" style={{ margin: 0 }}>
                    <AntSelect
                      size="small"
                      bordered={false}
                      className="mr-2 card-select"
                      options={filterAdnetwork.map(o => ({ value: o.code, label: o.name }))}
                      value={chartCountry}
                      style={{ width: 140 }}
                      onChange={onChangeChartCountry}
                    />
                    by Country
                  </div>
                  <div className="chart-data">
                    <AppChartCountry
                      startDate={startDate}
                      endDate={endDate}
                      code={code}
                      os={os}
                      campaign={campaign}
                      adnetwork={adnetwork}
                      country={country}
                      filter={chartCountry}
                      reload={load}
                    />
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default AppOverview;
